import {
  TeamCollectionApi,
  TeamCollectionApiMember,
} from '../../api/teamRelationship'
import {
  CollectionMetric,
  CollectionMetrics,
  EMPTY_COLLECTION_METRICS,
  TeamCollection,
  TeamMember,
} from './types'

const mapEngagedWith = (
  engagedWith: TeamCollectionApiMember['engagedWith']
): string[] => {
  if (!engagedWith?.length) {
    return []
  }
  return engagedWith
    .map(entry => {
      if (typeof entry === 'string') {
        return entry.trim()
      }
      return (entry?.name ?? '').trim()
    })
    .filter(Boolean)
}

const normalizeMetric = (
  metric?: Partial<CollectionMetric> | null,
  fallback: CollectionMetric = { count: 0, amount: 0 }
): CollectionMetric => ({
  count: metric?.count ?? fallback.count,
  amount: metric?.amount ?? fallback.amount,
})

export const mapCollectionApiMemberToTeamMember = (
  member: TeamCollectionApiMember
): TeamMember => ({
  id: member.memberId,
  name: (member.memberName ?? '').trim() || '-',
  role: (member.title ?? '').trim() || '-',
  location: member.location ?? '',
  avatarUrl: member.profileImageUrl,
  engagedWith: mapEngagedWith(member.engagedWith),
  metrics: {
    closedWon: normalizeMetric(member.metrics?.closedWon),
    inProgress: normalizeMetric(member.metrics?.inProgress),
  },
  lastActivity: null,
})

const mapCollectionMetrics = (
  metrics?: Partial<CollectionMetrics> | null
): CollectionMetrics => ({
  closedWon: normalizeMetric(
    metrics?.closedWon,
    EMPTY_COLLECTION_METRICS.closedWon
  ),
  inProgress: normalizeMetric(
    metrics?.inProgress,
    EMPTY_COLLECTION_METRICS.inProgress
  ),
  closedLost: normalizeMetric(
    metrics?.closedLost,
    EMPTY_COLLECTION_METRICS.closedLost
  ),
})

export const mapTeamCollectionApiToTeamCollection = (
  collection: TeamCollectionApi
): TeamCollection | null => {
  if (!collection?.id) {
    return null
  }
  const memberIds =
    collection.memberIds ??
    (collection.members ?? []).map(member => member.memberId).filter(Boolean)

  return {
    id: collection.id,
    name: (collection.name ?? '').trim() || '-',
    type: collection.type,
    memberIds: [...memberIds],
    createdAt: collection.createdAt ?? '',
    createdBy: collection.createdBy ?? '',
    metrics: mapCollectionMetrics(collection.metrics),
    isArchived: Boolean(collection.isArchived),
  }
}

export const mergeTeamCollectionApiIntoExisting = (
  existing: TeamCollection,
  collectionApi: TeamCollectionApi
): TeamCollection | null => {
  const mapped = mapTeamCollectionApiToTeamCollection(collectionApi)
  if (!mapped) {
    return null
  }

  return {
    ...existing,
    name: collectionApi.name !== undefined ? mapped.name : existing.name,
    type: collectionApi.type ?? existing.type,
    memberIds:
      collectionApi.memberIds !== undefined || collectionApi.members
        ? mapped.memberIds
        : existing.memberIds,
    createdAt: collectionApi.createdAt ?? existing.createdAt,
    createdBy: collectionApi.createdBy ?? existing.createdBy,
    metrics:
      collectionApi.metrics !== undefined ? mapped.metrics : existing.metrics,
    isArchived:
      collectionApi.isArchived !== undefined
        ? mapped.isArchived
        : existing.isArchived,
  }
}

export const mapTeamCollectionApisToTeamCollections = (
  collections: TeamCollectionApi[]
): TeamCollection[] =>
  collections
    .map(mapTeamCollectionApiToTeamCollection)
    .filter((collection): collection is TeamCollection => Boolean(collection))

export const mapCollectionApiMembersToTeamMembers = (
  members: TeamCollectionApiMember[]
): TeamMember[] =>
  members
    .filter(member => Boolean(member?.memberId))
    .map(mapCollectionApiMemberToTeamMember)
